import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, MapPin, Clock } from "lucide-react";
import { api, formatDate, mediaUrl } from "@/lib/api";
import { Reveal, Overline } from "@/components/Reveal";

const FILTERS = ["All", "Revival", "Book Signing", "Keynote"];

const TYPE_STYLES = {
  Revival: "bg-gold/15 text-gold-deep dark:text-gold-light",
  "Book Signing": "bg-sky-500/10 text-sky-600 dark:text-sky-400",
  Keynote: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
};

export const Events = ({ scrollToId }) => {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api
      .get("/events")
      .then((r) => setEvents(r.data))
      .catch(() => setEvents([]));
  }, []);

  const shown = useMemo(
    () => (filter === "All" ? events : events.filter((e) => e.type === filter)),
    [events, filter]
  );

  return (
    <section
      id="events"
      data-testid="events-section"
      className="relative py-24 sm:py-32 bg-parchment dark:bg-ink"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="max-w-2xl">
            <Reveal>
              <Overline>Events, Signings & Speaking</Overline>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
                Where to find Charles next.
              </h2>
              <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
                Upcoming revivals, book signings, and keynote engagements.
              </p>
            </Reveal>
          </div>

          <Reveal>
            <div className="flex flex-wrap gap-2">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  data-testid={`event-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                    filter === f
                      ? "bg-ink text-parchment border-ink dark:bg-gold dark:text-ink dark:border-gold"
                      : "border-border text-foreground/70 hover:border-gold"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {shown.map((e, i) => (
              <motion.article
                key={e.id}
                layout
                data-testid={`event-card-${e.id}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="flex flex-col p-6 rounded-2xl border border-border bg-card hover:-translate-y-1.5 transition-transform duration-300"
              >
                {e.media?.length > 0 && (
                  <div className="-mx-6 -mt-6 mb-5 h-44 overflow-hidden rounded-t-2xl">
                    {e.media[0].type === "video" ? (
                      <video
                        src={mediaUrl(e.media[0].url)}
                        controls
                        playsInline
                        preload="metadata"
                        className="w-full h-full object-cover bg-ink"
                      />
                    ) : (
                      <img
                        src={mediaUrl(e.media[0].url)}
                        alt={e.title}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      TYPE_STYLES[e.type] || "bg-secondary"
                    }`}
                  >
                    {e.type}
                  </span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {e.status}
                  </span>
                </div>

                <h3 className="mt-5 font-serif text-2xl font-semibold leading-tight">
                  {e.title}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed flex-1">
                  {e.summary}
                </p>

                <div className="mt-5 space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-foreground/80">
                    <Calendar size={15} className="text-gold-deep dark:text-gold-light" />
                    {formatDate(e.date)}
                    <Clock size={15} className="ml-2 text-gold-deep dark:text-gold-light" />
                    {e.time}
                  </div>
                  <div className="flex items-center gap-2 text-foreground/80">
                    <MapPin size={15} className="text-gold-deep dark:text-gold-light" />
                    {e.venue}, {e.location}
                  </div>
                </div>

                <button
                  data-testid={`event-rsvp-${e.id}`}
                  onClick={() => scrollToId("contact")}
                  className="mt-6 w-full py-2.5 rounded-full bg-ink text-parchment dark:bg-gold dark:text-ink text-sm font-semibold hover:bg-gold hover:text-ink dark:hover:bg-gold-light transition-colors"
                >
                  RSVP / Request Details
                </button>
              </motion.article>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
